/**
 * InquiryConvertModal - modal for converting inquiry to customer/booking
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { api } from '../../services/api';
import type { InquiryWithAssignment } from '../../services/inquiriesApi';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
}

interface InquiryConvertModalProps {
  inquiry: InquiryWithAssignment | null;
  isOpen: boolean;
  onClose: () => void;
  onConvert: (inquiryId: string, customerId: string) => Promise<void>;
}

export function InquiryConvertModal({
  inquiry,
  isOpen,
  onClose,
  onConvert,
}: InquiryConvertModalProps) {
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  // New customer form
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  // Pre-fill from inquiry
  useEffect(() => {
    if (inquiry) {
      setNewName(inquiry.name);
      setNewEmail(inquiry.email);
      setNewPhone(inquiry.phone || '');
      setSearchQuery(inquiry.email);
    }
  }, [inquiry]);

  // Search customers
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await api.get<{ items: Customer[] }>(
          `/api/customers?search=${encodeURIComponent(searchQuery)}&limit=5`
        );
        setSearchResults(results.items || []);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleConvert = async () => {
    if (!inquiry) return;

    setIsConverting(true);
    try {
      let customerId: string;

      if (mode === 'create') {
        // Create new customer
        const newCustomer = await api.post<Customer>('/api/customers', {
          name: newName,
          email: newEmail,
          phone: newPhone || undefined,
        });
        customerId = newCustomer.id;
      } else {
        if (!selectedCustomer) return;
        customerId = selectedCustomer.id;
      }

      await onConvert(inquiry.id, customerId);
      onClose();
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Convertir Consulta a Cliente</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Mode tabs */}
          <div className="flex gap-2 border-b pb-2">
            <button
              className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
                mode === 'search'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setMode('search')}
            >
              Buscar cliente existente
            </button>
            <button
              className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
                mode === 'create'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setMode('create')}
            >
              Crear nuevo cliente
            </button>
          </div>

          {mode === 'search' ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="search">Buscar por email o nombre</Label>
                <Input
                  id="search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedCustomer(null);
                  }}
                  placeholder="nombre@email.com"
                />
              </div>

              {isSearching && (
                <p className="text-sm text-muted-foreground">Buscando...</p>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-2">
                  <Label>Seleccionar cliente</Label>
                  {searchResults.map((customer) => (
                    <button
                      key={customer.id}
                      className={`w-full text-left p-3 border rounded-lg transition-colors ${
                        selectedCustomer?.id === customer.id
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setSelectedCustomer(customer)}
                    >
                      <p className="font-medium">{customer.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {customer.email}
                        {customer.phone && ` - ${customer.phone}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}

              {searchQuery.length >= 2 &&
                !isSearching &&
                searchResults.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No se encontraron clientes.{' '}
                    <button
                      className="text-primary hover:underline"
                      onClick={() => setMode('create')}
                    >
                      Crear nuevo cliente
                    </button>
                  </p>
                )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Juan Garcia"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="juan@email.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+34 600 000 000"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isConverting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConvert}
            disabled={
              isConverting ||
              (mode === 'search' && !selectedCustomer) ||
              (mode === 'create' && (!newName || !newEmail))
            }
          >
            {isConverting ? 'Convirtiendo...' : 'Convertir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
